import { useCallback, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';

const API_URL = import.meta.env.VITE_SERVICE;

function extractUrl(response) {
  if (typeof response === 'string') {
    try {
      response = JSON.parse(response);
    } catch {
      return response;
    }
  }
  return response?.data?.url ?? response?.url ?? null;
}

export function ImageUpload({
  value,
  onChange,
  accept = 'image/*',
  maxSizeMB = 5,
  shape = 'rounded',
  size = 'md',
  placeholder,
  className,
  disabled,
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const sizeClasses = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
    xl: 'h-40 w-40',
    full: 'h-32 w-full',
  };

  const shapeClasses = {
    rounded: 'rounded-2xl',
    circle: 'rounded-full',
    square: 'rounded-xl',
  };

  const upload = useCallback(
    (file) => {
      if (!file) return;

      if (file.size > maxSizeMB * 1024 * 1024) {
        toast.error(`Arquivo excede ${maxSizeMB}MB`);
        return;
      }

      setUploading(true);
      setProgress(0);

      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        setUploading(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            const url = extractUrl(response);
            if (url) {
              onChange?.(url);
            } else {
              toast.error('URL não encontrada na resposta');
            }
          } catch {
            toast.error('Falha ao processar resposta');
          }
        } else {
          toast.error('Falha no upload');
        }
      });

      xhr.addEventListener('error', () => {
        setUploading(false);
        toast.error('Erro de rede no upload');
      });

      xhr.open('POST', `${API_URL}/public-file/`);
      xhr.setRequestHeader(
        'Authorization',
        `Bearer ${localStorage.getItem('token')}`
      );
      xhr.send(formData);
    },
    [maxSizeMB, onChange]
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || uploading) return;
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) upload(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const hasImage = !!value;

  return (
    <div className={cn('relative inline-flex', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        disabled={disabled || uploading}
        className="hidden"
      />

      <button
        type="button"
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        disabled={disabled}
        className={cn(
          'group relative flex items-center justify-center overflow-hidden border-2 border-dashed transition-all duration-200',
          sizeClasses[size],
          shapeClasses[shape],
          disabled && 'cursor-not-allowed opacity-50',
          dragActive
            ? 'border-[var(--dash-primary)] bg-[var(--dash-primary-soft)]'
            : hasImage
              ? 'border-transparent'
              : 'border-[var(--dash-border-strong)] bg-[var(--dash-surface-subtle)] hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary-soft)]'
        )}
      >
        {hasImage ? (
          <>
            <img
              src={value}
              alt=""
              className={cn(
                'h-full w-full object-cover',
                shapeClasses[shape]
              )}
            />
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100',
                shapeClasses[shape]
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm">
                <Upload size={14} />
              </div>
              <button
                type="button"
                onClick={handleRemove}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-red-500/60"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </>
        ) : uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2
              size={size === 'sm' ? 16 : 20}
              className="animate-spin text-[var(--dash-primary)]"
            />
            <span className="text-[10px] font-medium tabular-nums text-[var(--dash-primary)]">
              {progress}%
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <ImagePlus
              size={size === 'sm' ? 16 : size === 'full' ? 22 : 20}
              className="text-[var(--dash-text-muted)] transition-colors group-hover:text-[var(--dash-primary)]"
            />
            {placeholder && size !== 'sm' && (
              <span className="px-2 text-center text-[10px] leading-tight text-[var(--dash-text-faint)]">
                {placeholder}
              </span>
            )}
          </div>
        )}
      </button>

      {uploading && (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-1 overflow-hidden',
            shape === 'circle' ? 'rounded-full' : 'rounded-b-xl'
          )}
          style={{ background: 'var(--dash-surface-hover)' }}
        >
          <div
            className="h-full transition-[width] duration-200"
            style={{
              width: `${progress}%`,
              background:
                'linear-gradient(90deg, #ad34fe 0%, #be5bff 100%)',
            }}
          />
        </div>
      )}
    </div>
  );
}
